import React, { type HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { FontWeight, FontFamily } from './Typography.types';
import { useTypography } from './TypographyContext';
import { getFontClass } from './typography.utils';

// add an enum for the variant
export enum TextVariant {
  H6 = 'h6',
  H7 = 'h7',
  H8 = 'h8',
  H9 = 'h9',
  H10 = 'h10',
  H11 = 'h11',
  H12 = 'h12',
  H13 = 'h13',
  H14 = 'h14',
}

export interface TextProps extends HTMLAttributes<HTMLParagraphElement> {
  fontWeight?: FontWeight;
  fontFamily?: FontFamily;
  children: React.ReactNode | string;
  variant: TextVariant;
  id?: string;
  /** never add override to the text size, line height, and weight */
  className?: string;
}

// Map variants to specific heading classes
const mapVariantToClass = {
  [TextVariant.H6]: 'text-[22px] leading-[28px] text-text-main-primary',
  [TextVariant.H7]: 'text-[20px] leading-[28px] text-text-main-primary',
  [TextVariant.H8]: 'text-[18px] leading-[22px] text-text-main-primary',
  [TextVariant.H9]: 'text-[16px] leading-[24px] text-text-main-primary',
  [TextVariant.H10]: 'text-[14px] leading-[20px] text-text-main-primary',
  [TextVariant.H11]: 'text-[13px] leading-[18px] text-text-main-primary',
  [TextVariant.H12]: 'text-[12px] leading-[16px] text-text-main-primary',
  [TextVariant.H13]: 'text-[11px] leading-[16px] text-text-main-primary',
  [TextVariant.H14]: 'text-[10px] leading-[15px] text-text-main-primary',
};

export const Text = (props: TextProps) => {
  const {
    fontWeight = FontWeight.REGULAR,
    fontFamily,
    children,
    variant = TextVariant.H6,
    className = '',
    id,
    ...rest
  } = props;

  const { defaultFontFamily } = useTypography();

  // Determine which font family to use: prop > context > none (for backward compatibility)
  const effectiveFontFamily = fontFamily || defaultFontFamily;

  const headingClass = mapVariantToClass[variant];
  const fontClass = getFontClass(fontWeight, effectiveFontFamily);
  const style = twMerge(headingClass, fontClass, className);

  return (
    <p className={style} id={id} {...rest}>
      {children}
    </p>
  );
};

Text.H6 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H6} />;
Text.H7 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H7} />;
Text.H8 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H8} />;
Text.H9 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H9} />;
Text.H10 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H10} />;
Text.H11 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H11} />;
Text.H12 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H12} />;
Text.H13 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H13} />;
Text.H14 = (props: Omit<TextProps, 'variant'>) => <Text {...props} variant={TextVariant.H14} />;
